export type DimensionUnit = 'cm' | 'in' | 'mm' | 'ft';
export type DimensionAxis = 'width' | 'depth' | 'length' | 'height';

export interface DimensionFeatureConfig {
  axes: DimensionAxis[];
  unit: DimensionUnit;
  required_axes?: DimensionAxis[];
}

export interface ProductDimensions {
  width?: number;
  depth?: number;
  length?: number;
  height?: number;
  unit: DimensionUnit;
}

const UNITS: DimensionUnit[] = ['cm', 'in', 'mm', 'ft'];
const AXES: DimensionAxis[] = ['width', 'depth', 'length', 'height'];

const AXIS_LABELS: Record<DimensionAxis, string> = {
  width: 'Width',
  depth: 'Depth',
  length: 'Length',
  height: 'Height',
};

export const AXIS_SHORT: Record<DimensionAxis, string> = {
  width: 'W',
  depth: 'D',
  length: 'L',
  height: 'H',
};

export const isDimensionUnit = (v: unknown): v is DimensionUnit =>
  typeof v === 'string' && UNITS.includes(v as DimensionUnit);

export const isDimensionAxis = (v: unknown): v is DimensionAxis =>
  typeof v === 'string' && AXES.includes(v as DimensionAxis);

export const isValidDimensionConfig = (v: unknown): v is DimensionFeatureConfig => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  if (!Array.isArray(o.axes) || o.axes.length === 0 || !o.axes.every(isDimensionAxis)) return false;
  if (!isDimensionUnit(o.unit)) return false;
  if (o.required_axes !== undefined) {
    if (!Array.isArray(o.required_axes) || !o.required_axes.every(isDimensionAxis)) return false;
    if (!o.required_axes.every((a) => (o.axes as DimensionAxis[]).includes(a))) return false;
  }
  return true;
};

export const parseDimensionConfig = (options: unknown): DimensionFeatureConfig | null =>
  isValidDimensionConfig(options) ? options : null;

export const formatProductDimensions = (d: ProductDimensions): string =>
  AXES.filter((a) => d[a] !== undefined && (d[a] ?? 0) > 0)
    .map((a) => `${d[a]} ${d.unit} ${AXIS_SHORT[a]}`)
    .join(' × ');

export const formatDimensionConfigLabel = (config: DimensionFeatureConfig): string =>
  `${config.axes.map((a) => AXIS_SHORT[a]).join('×')} · ${config.unit}`;

export const buildProductDimensions = (
  values: Partial<Record<DimensionAxis, number | ''>>,
  config: DimensionFeatureConfig,
): ProductDimensions => {
  const result: ProductDimensions = { unit: config.unit };
  config.axes.forEach((axis) => {
    const val = values[axis];
    if (typeof val === 'number' && val > 0) result[axis] = val;
  });
  return result;
};

export const validateProductDimensions = (
  values: Partial<Record<DimensionAxis, number | ''>>,
  config: DimensionFeatureConfig,
): string | null => {
  const required = config.required_axes?.length ? config.required_axes : config.axes;
  for (const axis of required) {
    const val = values[axis];
    if (val === undefined || val === '' || typeof val !== 'number' || val <= 0) {
      return `Missing or invalid ${AXIS_LABELS[axis].toLowerCase()}`;
    }
  }
  return null;
};

export const parseProductDimensions = (
  specs: Record<string, unknown> | null | undefined,
): ProductDimensions | null => {
  if (!specs || typeof specs !== 'object') return null;
  const nested = specs.dimensions;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null;
  const d = nested as Record<string, unknown>;
  const unit: DimensionUnit = isDimensionUnit(d.unit)
    ? d.unit
    : typeof d.width_in === 'number' ? 'in' : 'cm';
  const parsed: ProductDimensions = { unit };
  AXES.forEach((axis) => {
    const legacy = d[`${axis}_in`];
    const val = typeof legacy === 'number' && legacy > 0 ? legacy : d[axis];
    if (typeof val === 'number' && val > 0) parsed[axis] = val;
  });
  return AXES.some((a) => parsed[a] !== undefined) ? parsed : null;
};

export const ALL_DIMENSION_AXES = AXES;
export const ALL_DIMENSION_UNITS = UNITS;
